import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { apiUnscramble } from '../utils/api';
import { DICTIONARIES, loadDict, saveDict } from '../utils/dicts';
import SeoContent from '../components/SeoContent';
import DefinableWord from '../components/DefinableWord';
import DictInfo from '../components/DictInfo';
import PageSEO from '../components/PageSEO';

const clean = s => s.toLowerCase().replace(/[^a-z]/g, '');
// Keeps '?' so blank/wildcard tiles reach the solver.
const cleanLetters = s => s.toLowerCase().replace(/[^a-z?]/g, '');

export default function AnagramSolver() {
  const [letters, setLetters]   = useState('');
  const [dict, setDict]         = useState(loadDict);
  const [showFilters, setShowFilters] = useState(false);
  const [startsWith, setStartsWith]   = useState('');
  const [endsWith, setEndsWith]       = useState('');
  const [contains, setContains]       = useState('');
  const [lenFilter, setLenFilter]     = useState('any');

  const [data, setData]       = useState(null);   // raw API response
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  async function handleSolve(e) {
    e.preventDefault();
    const q = cleanLetters(letters);
    if (!q.replace(/\?/g, '')) return;
    setLoading(true); setError(''); setData(null);
    try {
      const { data } = await apiUnscramble(q, dict);
      setData(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong.');
    } finally { setLoading(false); }
  }

  function changeDict(next) {
    setDict(next);
    saveDict(next);
  }

  // Apply the advanced filters, then group by length (longest first).
  const { groups, total, availableLengths } = useMemo(() => {
    const results = data?.results || [];
    const sw = clean(startsWith), ew = clean(endsWith), ct = clean(contains);
    const lengthsSeen = new Set();
    const filtered = results.filter(r => {
      lengthsSeen.add(r.length);
      if (sw && !r.word.startsWith(sw)) return false;
      if (ew && !r.word.endsWith(ew)) return false;
      if (ct && !r.word.includes(ct)) return false;
      if (lenFilter !== 'any' && r.length !== Number(lenFilter)) return false;
      return true;
    });
    const byLen = {};
    filtered.forEach(r => { (byLen[r.length] = byLen[r.length] || []).push(r); });
    const grouped = Object.keys(byLen)
      .map(Number)
      .sort((a, b) => b - a)
      .map(len => ({ len, words: byLen[len].sort((a, b) => b.score - a.score || a.word.localeCompare(b.word)) }));
    return {
      groups: grouped,
      total: filtered.length,
      availableLengths: [...lengthsSeen].sort((a, b) => b - a),
    };
  }, [data, startsWith, endsWith, contains, lenFilter]);

  const hasQuery = data !== null;
  const dictLabel = DICTIONARIES.find(d => d.key === dict)?.label || dict;

  return (
    <div className="game-page">
      <PageSEO page="anagram-solver" />
      <div className="container" style={{maxWidth:760}}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">🔤</span>
          <h1>Anagram Solver</h1>
          <p>Solve any anagram in seconds — enter your letters and find all possible words you can make, sorted by length, scored, and filtered your way.</p>
        </div>

        <div className="solver-card">
          <form onSubmit={handleSolve}>
            <div className="mb-3">
              <label className="fw-bold mb-1">Your Letters</label>
              <input
                className="big-input"
                value={letters}
                onChange={e => setLetters(e.target.value.replace(/[^a-zA-Z?]/g,''))}
                placeholder="e.g. LISTEN or CA?"
                maxLength={15}
                autoFocus
              />
              <small className="text-muted fw-bold">Up to 15 letters. Use <code>?</code> for a blank/wildcard tile (max 2).</small>
            </div>

            {/* Dictionary selector */}
            <div className="mb-3">
              <label className="fw-bold mb-1 d-inline-flex align-items-center gap-2">
                Dictionary <DictInfo />
              </label>
              <select
                className="form-select"
                value={dict}
                onChange={e => changeDict(e.target.value)}
                style={{maxWidth:360, fontWeight:700}}
              >
                {DICTIONARIES.map(d => (
                  <option key={d.key} value={d.key}>{d.label}</option>
                ))}
              </select>
            </div>

            {/* Advanced filters toggle */}
            <button
              type="button"
              className="fw-bold d-inline-flex align-items-center gap-2 mb-3"
              onClick={() => setShowFilters(s => !s)}
              style={{
                background: showFilters ? '#eef0fb' : '#f8f9fc',
                border: '1.5px solid #e5e7f2', borderRadius: 999,
                padding: '0.45rem 0.95rem', color: 'var(--primary)',
                fontSize: '0.9rem', cursor: 'pointer',
              }}
            >
              <i className="bi bi-sliders"></i>
              Advanced filters
              <i className={`bi bi-chevron-${showFilters ? 'up' : 'down'}`} style={{fontSize:'0.8rem'}}></i>
            </button>

            {showFilters && (
              <div className="row g-2 mb-4 p-3" style={{background:'#f8f9fc', borderRadius:12, border:'1px solid #eef0fb'}}>
                <div className="col-6 col-md-3">
                  <label className="small fw-bold mb-1">Starts with</label>
                  <input className="form-control" value={startsWith}
                    onChange={e => setStartsWith(e.target.value.replace(/[^a-zA-Z]/g,''))}
                    placeholder="e.g. le" maxLength={10}/>
                </div>
                <div className="col-6 col-md-3">
                  <label className="small fw-bold mb-1">Ends with</label>
                  <input className="form-control" value={endsWith}
                    onChange={e => setEndsWith(e.target.value.replace(/[^a-zA-Z]/g,''))}
                    placeholder="e.g. on" maxLength={10}/>
                </div>
                <div className="col-6 col-md-3">
                  <label className="small fw-bold mb-1">Contains</label>
                  <input className="form-control" value={contains}
                    onChange={e => setContains(e.target.value.replace(/[^a-zA-Z]/g,''))}
                    placeholder="e.g. is" maxLength={10}/>
                </div>
                <div className="col-6 col-md-3">
                  <label className="small fw-bold mb-1">Word length</label>
                  <select className="form-select" value={lenFilter} onChange={e => setLenFilter(e.target.value)}>
                    <option value="any">Any</option>
                    {availableLengths.map(l => <option key={l} value={l}>{l} letters</option>)}
                  </select>
                </div>
              </div>
            )}

            <div style={{borderTop:'1px solid #eef0fb', paddingTop:'1.1rem', marginTop:'0.25rem'}}>
              <button type="submit" className="solve-btn" disabled={loading || !cleanLetters(letters).replace(/\?/g,'')}>
                {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Solving…</> : '🔤 Solve Anagram'}
              </button>
            </div>
          </form>
        </div>

        {error && <div className="alert alert-danger alert-custom">{error}</div>}

        {hasQuery && (
          <div className="solver-card">
            <div className="d-flex flex-wrap align-items-baseline justify-content-between gap-2 mb-3">
              <div>
                <span className="fw-bold" style={{fontSize:'1.15rem'}}>
                  {total === 0 ? 'No words found' : <>We found <span style={{color:'var(--primary)'}}>{total}</span> word{total !== 1 ? 's' : ''}</>}
                </span>
                {total > 0 && (
                  <div className="text-muted small fw-bold">
                    from the letters <strong style={{letterSpacing:1}}>{cleanLetters(letters).toUpperCase()}</strong>
                  </div>
                )}
              </div>
              <span className="badge rounded-pill" style={{background:'#eef0fb', color:'var(--primary)', fontWeight:800, padding:'0.5em 0.9em'}}>
                📖 {dictLabel}
              </span>
            </div>

            {total === 0 ? (
              <div className="text-center py-3">
                <div style={{fontSize:'2.5rem'}}>🧐</div>
                <p className="text-muted fw-bold mb-0">No words match these letters and filters.</p>
              </div>
            ) : (
              <>
                {groups.map(g => (
                  <div key={g.len} className="mb-3">
                    <div className="fw-bold mb-2 pb-1" style={{
                      borderBottom:'2px solid #eef0fb', color:'#334155', fontSize:'1rem',
                    }}>
                      {g.len}-Letter Words <span className="text-muted fw-bold">({g.words.length})</span>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {g.words.map(r => (
                        <span key={r.word} className="anagram-chip d-inline-flex align-items-center"
                          style={{
                            background:'#fff', border:'1.5px solid #e5e7f2', borderRadius:10,
                            padding:'0.35rem 0.6rem', lineHeight:1,
                          }}>
                          <DefinableWord
                            word={r.word}
                            dict={dict}
                            className="fw-bold"
                            style={{fontSize:'1rem', letterSpacing:1, textTransform:'uppercase', textDecoration:'none', color:'#1e293b'}}
                          >
                            {r.word.toUpperCase()}
                          </DefinableWord>
                          <sup className="ms-1" style={{color:'var(--primary)', fontWeight:800, fontSize:'0.68rem'}}>{r.score}</sup>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
                <p className="text-muted small fw-bold mb-0 mt-2">
                  💡 Small number = Scrabble points. Click any word to see its meaning.
                </p>
              </>
            )}
          </div>
        )}
      <SeoContent game="anagram-solver" />
      </div>
    </div>
  );
}
